import {
  ArrowLongLeftIcon,
  Button,
  EyeIcon,
  EyeSlashIcon,
  Input,
  KeyIcon,
  Link,
  LockClosedIcon,
  Navigate,
  routeToUrl,
  success,
  useCustomForm,
  useState,
} from "@";
import { resetPassword } from "./utils/api";
import { resetPassSchema } from "./utils/validation";

const ResetPassword = () => {
  if (!localStorage.getItem("token")) {
    return <Navigate to="/check-otp" />;
  }

  const [showPassword, setShowPassword] = useState(false);

  const { errors, register, handleSubmit, isSubmitting, watch } = useCustomForm(
    {},
    resetPassSchema,
  );

  const submit = async (data) => {
    try {
      await resetPassword(data);
      success("تتم إعادة ضبط كلمة المرور الجديدة بنجاح");
      localStorage.removeItem("phoneNumber");
      localStorage.removeItem("token");
      routeToUrl("/login");
    } catch (error) {}
  };

  // Simple password strength heuristic
  const password = watch ? watch("newPassword") || "" : "";
  const strength = (() => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    return score;
  })();
  const strengthLabel = ["ضعيفة جداً", "ضعيفة", "متوسطة", "جيدة", "قوية"][
    strength
  ];
  const strengthTone = [
    "from-rose-400 to-rose-600",
    "from-rose-400 to-rose-600",
    "from-amber-400 to-amber-600",
    "from-emerald-400 to-emerald-600",
    "from-emerald-500 to-emerald-700",
  ][strength];
  const strengthText = [
    "text-rose-700 dark:text-rose-300",
    "text-rose-700 dark:text-rose-300",
    "text-amber-700 dark:text-amber-300",
    "text-emerald-700 dark:text-emerald-300",
    "text-emerald-700 dark:text-emerald-300",
  ][strength];

  return (
    <div className="relative p-6 md:p-8">
      {/* Step indicator */}
      <div className="mb-5 flex items-center gap-2">
        <span className="inline-flex h-7 items-center gap-1.5 rounded-full bg-purple-50 px-2.5 text-[11px] font-bold uppercase tracking-[0.14em] text-purple-700 ring-1 ring-purple-100 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-800/40">
          <KeyIcon className="h-3 w-3" />
          استعادة الحساب
        </span>
        <div className="flex items-center gap-1">
          <span className="h-1.5 w-2 rounded-full bg-purple-300 dark:bg-purple-600" />
          <span className="h-1.5 w-2 rounded-full bg-purple-300 dark:bg-purple-600" />
          <span className="h-1.5 w-6 rounded-full bg-purple-700 dark:bg-purple-400" />
        </div>
      </div>

      <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100 md:text-3xl">
        إعادة ضبط كلمة المرور
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-gray-500 dark:text-gray-400">
        أدخل كلمة مرور جديدة وقوية لحمايتك من الوصول غير المصرح به.
      </p>

      <form className="mt-7 space-y-5" onSubmit={handleSubmit(submit)}>
        <div className="relative *:mb-12 *:h-14 *:rounded-full">
          <Input
            star
            label="كلمة المرور الجديدة"
            error={errors.newPassword}
            input={register("newPassword")}
            type={showPassword ? "text" : "password"}
            styles="input ps-10 pe-10 h-14 rounded-full  dark:bg-transparent dark:border-gray-500"
          />
          <span
            aria-hidden
            className="pointer-events-none absolute start-3 top-[42px] text-gray-400 dark:text-gray-400"
          >
            <LockClosedIcon className="h-6 w-6" />
          </span>
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={
              showPassword ? "إخفاء كلمة المرور" : "إظهار كلمة المرور"
            }
            className="absolute end-2 top-[33px] flex h-9 w-9 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-50 hover:text-purple-700 dark:text-gray-500 dark:hover:bg-gray-700 dark:hover:text-purple-300"
          >
            {showPassword ? (
              <EyeSlashIcon className="h-6 w-6" />
            ) : (
              <EyeIcon className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Strength meter */}
        {password && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-gray-500 dark:text-gray-400">
                قوة كلمة المرور
              </span>
              <span
                className={`text-[11px] font-bold tracking-tight ${strengthText}`}
              >
                {strengthLabel}
              </span>
            </div>
            <div className="flex gap-1">
              {[0, 1, 2, 3].map((i) => (
                <span
                  key={i}
                  className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                    i < strength
                      ? `bg-gradient-to-r ${strengthTone}`
                      : "bg-gray-100 dark:bg-gray-700"
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        <Button
          type="submit"
          isLoading={isSubmitting}
          title="إعادة التعيين"
          className="!w-full rounded-full !py-3"
        />

        <div className="flex items-center justify-center gap-1 border-t border-gray-100 pt-5 dark:border-gray-700/60">
          <Link
            to="/login"
            className="group/link inline-flex items-center gap-1 text-xs font-semibold tracking-tight text-purple-700 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300"
          >
            تسجيل الدخول إلى حسابك
            <ArrowLongLeftIcon className="h-3.5 w-3.5 transition-transform group-hover/link:-translate-x-0.5 rtl:rotate-180" />
          </Link>
        </div>
      </form>
    </div>
  );
};

export default ResetPassword;
